import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Sheet } from '../../components/ui/Sheet';
import { TermsOfServicePage } from '../legal/TermsOfServicePage';
import { EnableExpressBookingDrawer } from '../../components/expressBooking/EnableExpressBookingDrawer';
import { Loader } from '../../components/ui/Loader';
import apiClient from '../../api/client';
import { API_ENDPOINTS } from '../../api/endpoints';
import { useArtistDetailsStore } from '@/stores/artistDetailsStore';
import {
  ChevronRight,
  FileText,
  Lock,
  MapPin,
  Plus,
  ScrollText,
  ChevronDown,
  Trash2,
  User,
  Smartphone,
  ClipboardList,
  BriefcaseBusiness,
  BarChart3,
  LayoutGrid,
  UserX,
  Sparkles,
  Bell,
  Info,
  Mail,
  SquareArrowOutUpRight,
  AlertCircle,
  Phone,
  Eye,
} from 'lucide-react';
import { EXPRESS_BOOKING_GIF_URL } from '../../constants/expressBookingAssets';

const MASKED_APPROVAL_PHONES = ['+91- 9599******', '+91- 7042******'] as const;

const FULL_APPROVAL_PHONES = [
  { display: '+91- 9599122737', tel: '+919599122737' },
  { display: '+91- 7042884270', tel: '+917042884270' },
] as const;

type InstantApprovalContactsProps = {
  isPremiumOrElite: boolean;
  onMembershipClick: () => void;
  embedded?: boolean;
};

const InstantApprovalContacts: React.FC<InstantApprovalContactsProps> = ({
  isPremiumOrElite,
  onMembershipClick,
  embedded = false,
}) => {
  const handleEyeClick = () => {
    toast('Only visible to Premium/Elite members');
  };

  return (
    <div
      className={[
        'flex flex-col items-center gap-2 mx-auto text-center text-xs text-[#8E8E93]',
        embedded
          ? 'mt-3 w-full border-t border-white/10 pt-3'
          : 'mt-3 w-full max-w-sm',
      ].join(' ')}
    >
      <p>For instant approval, you can dial:</p>
      {isPremiumOrElite ? (
        <div className="flex w-full flex-col items-center gap-2">
          {FULL_APPROVAL_PHONES.map((phone) => (
            <a
              key={phone.tel}
              href={`tel:${phone.tel}`}
              className="inline-flex items-center justify-center gap-2 break-all hover:text-white transition-colors"
            >
              <Phone className="h-3.5 w-3.5 shrink-0 text-[#8E8E93]" />
              <span>{phone.display}</span>
            </a>
          ))}
        </div>
      ) : (
        <>
          <div className="flex w-full flex-col items-center gap-2">
            {MASKED_APPROVAL_PHONES.map((maskedNumber) => (
              <div
                key={maskedNumber}
                className="inline-flex items-center justify-center gap-2 break-all"
              >
                <Phone className="h-3.5 w-3.5 shrink-0 text-[#8E8E93]" />
                <span>{maskedNumber}</span>
                <button
                  type="button"
                  aria-label="Phone number visibility information"
                  onClick={handleEyeClick}
                  className="shrink-0 rounded-full p-0.5 hover:bg-white/5 transition-colors"
                >
                  <Eye className="h-3.5 w-3.5 text-[#8E8E93]" />
                </button>
              </div>
            ))}
          </div>
          <p>
            Only visible to{' '}
            <button
              type="button"
              onClick={onMembershipClick}
              className="text-[#F33363] underline underline-offset-2 hover:text-[#ff5c82] transition-colors"
            >
              Premium/Elite
            </button>{' '}
            members.
          </p>
        </>
      )}
    </div>
  );
};

export const ExpressBookingEnablePage: React.FC = () => {
  type ExpressBookingLocation = {
    id: number;
    pincode: string;
    city: string;
    state: string;
    complete_address: string;
    landmark?: string;
    created_at?: string;
    updated_at?: string;
  };

  type ExpressBookingRejectedLocation = {
    location_id: number;
    pincode: string;
    city: string;
    state: string;
    complete_address: string;
    landmark?: string | null;
  };

  const EXPRESS_BOOKING_ENDPOINT = API_ENDPOINTS.EXPRESS_BOOKING.BASE;
  const navigate = useNavigate();
  const ebEnabledStatus = useArtistDetailsStore((s) => s.data?.eb_enabled);
  const subscriptionName = useArtistDetailsStore(
    (s) => s.data?.subscriptions?.subscription_name?.toLowerCase() ?? '',
  );

  const [isEnabled, setIsEnabled] = useState(false);
  const [ebEnabledLabel, setEbEnabledLabel] = useState<string | undefined>();
  const [expressBookingEbEnabled, setExpressBookingEbEnabled] = useState<number | undefined>();
  const [locations, setLocations] = useState<ExpressBookingLocation[]>([]);
  const [hasRejectedLocation, setHasRejectedLocation] = useState(false);
  const [rejectedLocation, setRejectedLocation] = useState<ExpressBookingRejectedLocation | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isToggling, setIsToggling] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<ExpressBookingLocation | null>(null);
  const [locationInfoOpenId, setLocationInfoOpenId] = useState<number | null>(null);
  const [enableDrawerOpen, setEnableDrawerOpen] = useState(false);
  const [termsSheetOpen, setTermsSheetOpen] = useState(false);
  const [privacySheetOpen, setPrivacySheetOpen] = useState(false);
  const [privacyIntroExpanded, setPrivacyIntroExpanded] = useState(false);
  const [expandedPrivacyCardId, setExpandedPrivacyCardId] = useState<number | null>(null);

  const privacyItems = [
    {
      n: 1,
      title: 'Information We Collect',
      expanded: (
        <>
          <p className="text-sm leading-relaxed text-white/60">
            When you use the StarClinch Website and App, we collect different types of information.
          </p>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <User className="h-5 w-5 text-[#F33363]" />
              <div className="mt-3 text-sm font-semibold text-white">Personal Information</div>
              <div className="mt-1 text-xs leading-relaxed text-white/60">
                When you use the StarClinch Website and App, we collect information you provide to
                us. To log in or register for the App, we collect information such as your Facebook
                ID, name, email address, profile picture, and other publicly available Facebook profile
                data.
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <Smartphone className="h-5 w-5 text-[#F33363]" />
              <div className="mt-3 text-sm font-semibold text-white">
                Automatically Collected Information
              </div>
              <div className="mt-1 text-xs leading-relaxed text-white/60">
                We may collect device information, IP address, browser type, and other technical data
                when you access our platforms.
              </div>
            </div>
          </div>

          <div className="mt-3 rounded-2xl border border-white/10 bg-black/20 p-4">
            <ClipboardList className="h-5 w-5 text-[#F33363]" />
            <div className="mt-3 text-sm font-semibold text-white">Express Booking Data</div>
            <div className="mt-1 text-xs leading-relaxed text-white/60">
              We collect specific data related to Express Booking usage, including response times,
              attendance records, performance ratings, client feedback, and real-time location (used
              for attendance verification, safety confirmation, and dispute resolution).
            </div>
          </div>
        </>
      ),
    },
    {
      n: 2,
      title: 'Use of Information',
      expanded: (
        <>
          <p className="text-sm leading-relaxed text-white/60">
            We use the information we collect for the following purposes:
          </p>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <BriefcaseBusiness className="h-5 w-5 text-[#F33363]" />
              <div className="mt-3 text-sm font-semibold text-white">Providing Services</div>
              <div className="mt-1 text-xs leading-relaxed text-white/60">
                The StarClinch Website and App enable artists to create profiles, get discovered, and
                be booked by clients. We maintain artist profiles on both platforms to promote talent.
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <BarChart3 className="h-5 w-5 text-[#F33363]" />
              <div className="mt-3 text-sm font-semibold text-white">Analytics and Improvement</div>
              <div className="mt-1 text-xs leading-relaxed text-white/60">
                We use Google Analytics to analyze Website traffic and improve our services.
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <LayoutGrid className="h-5 w-5 text-[#F33363]" />
              <div className="mt-3 text-sm font-semibold text-white">Content Display</div>
              <div className="mt-1 text-xs leading-relaxed text-white/60">
                Images and videos imported to our platforms, if approved by our internal team, may
                be displayed on your profile.
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
              <UserX className="h-5 w-5 text-[#F33363]" />
              <div className="mt-3 text-sm font-semibold text-white">Account Deletion</div>
              <div className="mt-1 text-xs leading-relaxed text-white/60">
                Your data will be deleted when you delete your account, though some information may
                be retained for legal and compliance purposes.
              </div>
            </div>
          </div>

          <div className="mt-3 rounded-2xl border border-white/10 bg-black/20 p-4">
            <Sparkles className="h-5 w-5 text-[#F33363]" />
            <div className="mt-3 text-sm font-semibold text-white">Feature Optimization</div>
            <div className="mt-1 text-xs leading-relaxed text-white/60">
              Data collected via Express Booking is strictly used to calculate reliability scores,
              ensure artist safety, and determine fair and data-driven visibility rankings within the
              booking algorithm.
            </div>
          </div>
        </>
      ),
    },
    {
      n: 3,
      title: 'Communications',
      expanded: (
        <>
          <p className="text-sm leading-relaxed text-white/60">
            We use the information we collect for the following purposes:
          </p>

          <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 p-4">
            <Bell className="h-5 w-5 text-[#F33363]" />
            <div className="mt-3 text-sm font-semibold text-white">Operational Alerts</div>
            <div className="mt-1 text-xs leading-relaxed text-white/60">
              Users of Express Booking consent to receive mandatory, time-sensitive operational
              notifications via SMS, Email, WhatsApp, or Push Notifications necessary for the timely
              fulfillment of the contracted gig.
            </div>
          </div>
        </>
      ),
    },
    {
      n: 4,
      title: 'Data Deletion Instructions',
      expanded: (
        <>
          <p className="text-sm leading-relaxed text-white/60">
            If you want to delete your data that you have imported from Instagram, follow these
            steps:
          </p>

          <div className="mt-4 space-y-3">
            {[
              'Go to your Instagram Account’s “Settings & Privacy”.',
              'Click “Settings”.',
              'Then click “Website permissions”.',
              'Look for “Apps and Websites” and you will see all the apps and websites you have linked with your Instagram account.',
              'Search and click “Artist Dashboard”.',
              'Scroll and click “Remove”.',
            ].map((text, idx) => (
              <div key={text} className="flex gap-3">
                <div className="w-5 text-sm font-semibold text-[#F33363]">{idx + 1}</div>
                <div className="text-xs leading-relaxed text-white/60">{text}</div>
              </div>
            ))}
          </div>
        </>
      ),
    },
    {
      n: 5,
      title: 'Contact Information',
      expanded: (
        <>
          <p className="text-sm leading-relaxed text-white/60">
            If you have any questions or concerns regarding this Privacy Policy ..
          </p>

          <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 p-4">
            <Mail className="h-5 w-5 text-[#F33363]" />
            <div className="mt-3 text-sm font-semibold text-white">
              Please contact Ms. Shambhavi Tiwari at
            </div>
            <div className="mt-1 flex items-center gap-2 text-sm text-white/70">
              <span>connect@starclinch.com</span>
              <SquareArrowOutUpRight className="h-4 w-4 text-[#F33363]" />
            </div>
            <div className="mt-3 text-xs leading-relaxed text-white/60">
              StarClinch® is a registered trademark.
            </div>
          </div>
        </>
      ),
    },
  ];

  const fetchDetailsData = useCallback(async () => {
    const res = await apiClient.get(API_ENDPOINTS.AUTH.ARTIST_DETAILS);
    const details = res?.data?.data;
    if (details) {
      useArtistDetailsStore.getState().setArtistDetails(details);
    }
    return Boolean(details?.eb);
  }, []);

  const fetchExpressBookingData = useCallback(async () => {
    const res = await apiClient.get(EXPRESS_BOOKING_ENDPOINT);
    const data = res?.data?.data;
    return {
      locations: Array.isArray(data?.locations) ? data.locations : [],
      ebEnabledLabel:
        typeof data?.eb_enabled_label === 'string' ? data.eb_enabled_label : undefined,
      ebEnabled:
        typeof data?.eb_enabled === 'number' ? data.eb_enabled : undefined,
      hasRejectedLocation: data?.has_rejected_location === true,
      rejectedLocation:
        data?.has_rejected_location === true && data?.rejected_location
          ? data.rejected_location
          : null,
    };
  }, [EXPRESS_BOOKING_ENDPOINT]);

  const refreshExpressBookingData = useCallback(async () => {
    const [ebEnabled, expressBookingData] = await Promise.all([
      fetchDetailsData(),
      fetchExpressBookingData(),
    ]);
    setIsEnabled(ebEnabled);
    setLocations(expressBookingData.locations);
    setEbEnabledLabel(expressBookingData.ebEnabledLabel);
    setExpressBookingEbEnabled(expressBookingData.ebEnabled);
    setHasRejectedLocation(expressBookingData.hasRejectedLocation);
    setRejectedLocation(expressBookingData.rejectedLocation);
  }, [fetchDetailsData, fetchExpressBookingData]);

  useEffect(() => {
    // React dev StrictMode remounts components; to avoid double API calls, we dedupe only
    // the initial network fetch and let *each* remount apply the returned data to its own state.
    const key = '__cockpit_express_booking_enable_initial_load_data_promise__';
    let cancelled = false;

    const run = async () => {
      setIsLoading(true);
      try {
        if (!(globalThis as any)[key]) {
          (globalThis as any)[key] = Promise.all([fetchDetailsData(), fetchExpressBookingData()]).then(
            ([ebEnabled, expressBookingData]) => ({ ebEnabled, expressBookingData })
          );
          (globalThis as any)[key] = (globalThis as any)[key].finally(() => {
            delete (globalThis as any)[key];
          });
        }

        const data = await (globalThis as any)[key];
        if (cancelled) return;
        setIsEnabled(data.ebEnabled);
        setLocations(data.expressBookingData.locations);
        setEbEnabledLabel(data.expressBookingData.ebEnabledLabel);
        setExpressBookingEbEnabled(data.expressBookingData.ebEnabled);
        setHasRejectedLocation(data.expressBookingData.hasRejectedLocation);
        setRejectedLocation(data.expressBookingData.rejectedLocation);
      } catch {
        if (!cancelled) toast.error('Failed to load Express Booking details.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [fetchDetailsData, fetchExpressBookingData]);

  const visibleLocations = useMemo<ExpressBookingLocation[]>(() => {
    if (!Array.isArray(locations) || locations.length === 0) return [];
    if (locations.length === 1) return locations;

    const toEpoch = (value?: string) => {
      if (!value) return Number.NEGATIVE_INFINITY;
      const ts = Date.parse(value);
      return Number.isFinite(ts) ? ts : Number.NEGATIVE_INFINITY;
    };

    const sorted = [...locations].sort((a, b) => {
      const aTs = Math.max(toEpoch(a.updated_at), toEpoch(a.created_at));
      const bTs = Math.max(toEpoch(b.updated_at), toEpoch(b.created_at));
      return bTs - aTs;
    });

    return sorted.slice(0, 1);
  }, [locations]);

  const rejectedLocationMessage = useMemo(() => {
    if (!hasRejectedLocation || !rejectedLocation) return null;
    const place = [rejectedLocation.city, rejectedLocation.state].filter(Boolean).join(', ');
    if (!place) {
      return 'Your work location request has been rejected. It has been reverted to your previous location';
    }
    return `${place} work location request has been rejected. It has been reverted to your previous location`;
  }, [hasRejectedLocation, rejectedLocation]);

  const handleToggleEnabled = async () => {
    if (isToggling) return;
    const prevEnabled = isEnabled;
    const nextEnabled = !isEnabled;
    setIsEnabled(nextEnabled);
    setIsToggling(true);
    try {
      await apiClient.patch(EXPRESS_BOOKING_ENDPOINT, { eb_enabled: nextEnabled });
      await refreshExpressBookingData();
      toast.success(
        nextEnabled ? 'Express Booking enabled successfully.' : 'Express Booking disabled successfully.'
      );
    } catch (e: any) {
      setIsEnabled(prevEnabled);
      toast.error(
        e?.response?.data?.message ||
        (nextEnabled
          ? 'Failed to enable Express Booking. Please fill the consent form'
          : 'Failed to disable Express Booking. Please try again.')
      );
    } finally {
      setIsToggling(false);
    }
  };

  const handleDeleteLocation = async () => {
    if (!locationToDelete || isDeleting) return;
    setIsDeleting(true);
    try {
      await apiClient.delete(
        API_ENDPOINTS.EXPRESS_BOOKING.LOCATION_DELETE(locationToDelete.id)
      );
      setLocationToDelete(null);
      await refreshExpressBookingData();
      toast.success('Location deleted successfully.');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to delete location. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const consentRows = useMemo(
    () => [
      {
        id: 'consent-form',
        label: 'Consent Form',
        icon: FileText,
        onClick: () => navigate('/express-booking-consent-form'),
      },
      {
        id: 'privacy',
        label: 'Privacy Policy',
        icon: Lock,
        onClick: () => setPrivacySheetOpen(true),
      },
      {
        id: 't-c',
        label: 'T&C',
        icon: ScrollText,
        onClick: () => setTermsSheetOpen(true),
      },
    ],
    [navigate],
  );

  const resolvedEbEnabled =
    expressBookingEbEnabled ?? ebEnabledStatus;
  const isUnderReview = resolvedEbEnabled === 2;
  const isEbRejected =
    resolvedEbEnabled === 0 && ebEnabledLabel?.toLowerCase() === 'rejected';
  const isConsentRejected = isEbRejected;
  const isPremiumOrElite =
    subscriptionName.includes('premium') || subscriptionName.includes('elite');

  return (
    <div className="w-full py-6 pb-24 text-white">
      {/* Top card */}
      <section className="w-full rounded-[2rem] border border-white/10 bg-[linear-gradient(135deg,rgba(88, 85, 85, 0.06),rgba(10,10,12,0.35))] p-5">
        <div>
          <div className="flex items-start justify-between gap-4">
            <div className="relative mt-2 h-20 w-36 shrink-0">
              <img
                src={EXPRESS_BOOKING_GIF_URL}
                alt="Express Booking"
                className="pointer-events-none absolute inset-0 h-full w-full object-contain object-top"
              />
            </div>

            {!isUnderReview ? (
              <button
                type="button"
                onClick={handleToggleEnabled}
                aria-pressed={isEnabled}
                disabled={isToggling || isLoading}
                className={[
                  'mt-1 relative w-14 h-8 shrink-0 rounded-full transition-colors disabled:opacity-60',
                  isEnabled ? 'bg-[#F33363]' : 'bg-[#2C2C2E]',
                ].join(' ')}
              >
                <span
                  className={[
                    'absolute top-1 w-6 h-6 rounded-full transition-transform',
                    isEnabled ? 'left-7 bg-white' : 'left-1 bg-[#8E8E93]',
                  ].join(' ')}
                />
              </button>
            ) : (
              <div className="mt-1 w-14 shrink-0" aria-hidden />
            )}
          </div>

          <p className="mt-3 text-sm text-[#8E8E93] leading-relaxed">
            When Express Booking is ON, you may receive high-priority booking requests for nearby events. You’re always in control - turn it off whenever you can’t take quick bookings.
          </p>
        </div>

        {isConsentRejected && (
          <div className="mt-4 rounded-2xl border border-[#F33363]/25 bg-[#4B0017]/25 px-4 py-3 text-center">
            <p className="text-sm font-semibold text-white">
              <AlertCircle className="inline h-4 w-4 text-[#F33363] mr-1.5 -mt-0.5" />
              Your Express Booking Consent Request has been rejected.{' '}
              <button
                type="button"
                onClick={() => navigate('/express-booking-consent-form')}
                className="text-[#F33363] underline underline-offset-2"
              >
                Fill it again!
              </button>
            </p>
            <InstantApprovalContacts
              embedded
              isPremiumOrElite={isPremiumOrElite}
              onMembershipClick={() => navigate('/membership')}
            />
          </div>
        )}

        {!isConsentRejected && (
          <div className="mt-4 flex flex-col items-center gap-3">
            {isUnderReview ? (
              <div className="w-full max-w-md mx-auto rounded-2xl border border-[#F33363] px-5 py-4 shadow-[0_12px_30px_rgba(243,51,99,0.45)] text-center">
                <p className="text-sm font-bold uppercase tracking-widest text-white">
                  Your Eligibility is currently under review
                </p>
                <InstantApprovalContacts
                  embedded
                  isPremiumOrElite={isPremiumOrElite}
                  onMembershipClick={() => navigate('/membership')}
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => navigate('/jobs', { state: { tab: 'gigs' } })}
                className="inline-flex items-center justify-center uppercase tracking-widest rounded-2xl px-7 py-3 text-sm font-bold text-white shadow-[0_12px_30px_rgba(243,51,99,0.45)] border border-[#F33363] mb-1"
              >
                Browse Jobs
              </button>
            )}
            {/* <div className="text-xs font-bold text-[#34D399]">65 Jobs Available!</div> */}
          </div>
        )}

        {/* Locations */}

      </section>
      {!isConsentRejected && (
      <div className="mt-6 rounded-[1.25rem]">
        <div className="mb-3 flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-sm font-bold text-white/90">Your Work Location</span>
          {rejectedLocationMessage && (
            <span className="text-sm font-semibold text-[#F33363] leading-relaxed">
              ({rejectedLocationMessage})
            </span>
          )}
        </div>
        <div className="space-y-3">
          {isLoading ? (
            <div className="flex justify-center py-6">
              <Loader className="h-6 w-6 text-[#F33363]" />
            </div>
          ) : visibleLocations.length === 0 ? (
            <div className="text-xs text-[#8E8E93]">Add a location to enable express booking.</div>
          ) : (
            visibleLocations.map((location) => (
              <div
                key={location.id}
                className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.06),rgba(10,10,12,0.35))] px-4 py-3"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <MapPin className="h-4 w-4 shrink-0 text-[#8E8E93]" />
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-white">{location.pincode}</div>
                    <div className="text-xs text-[#8E8E93] truncate">
                      {[location.city, location.state].filter(Boolean).join(', ')}
                    </div>
                  </div>
                </div>

                <div className="min-w-0 max-w-[62%]">
                  <div className="flex items-center justify-end gap-2 text-xs text-[#8E8E93]">
                    <button
                      type="button"
                      aria-label={`Remove location ${location.pincode}`}
                      onClick={() => setLocationToDelete(location)}
                      className="shrink-0 rounded-xl p-2 hover:bg-white/5 transition-colors"
                    >
                      <Trash2 className="h-4 w-4 text-[#F33363]" />
                    </button>
                    <span className="leading-relaxed break-words">
                      Delete this Location to add a New Location
                    </span>
                    <button
                      type="button"
                      aria-label={`Location change information for ${location.pincode}`}
                      onClick={() =>
                        setLocationInfoOpenId((current) => (current === location.id ? null : location.id))
                      }
                      className="shrink-0 rounded-full p-1 hover:bg-white/5 transition-colors"
                    >
                      <Info className="h-3.5 w-3.5 text-white/70" />
                    </button>
                  </div>

                  {locationInfoOpenId === location.id && (
                    <div className="mt-1.5 text-[11px] leading-relaxed text-white/80 break-words text-right">
                      Change the Location only if you're re-locating
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {visibleLocations.length === 0 && (
          <button
            type="button"
            onClick={() => setEnableDrawerOpen(true)}
            disabled={isLoading}
            className="mt-4 w-full rounded-2xl bg-[#F33363] px-4 py-3 text-sm font-bold text-white uppercase tracking-widest inline-flex items-center justify-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add New Location
          </button>
        )}
      </div>
      )}
      {/* Consent / policies list */}
      <section className="mt-4 space-y-4">
        {consentRows.map((row) => (
          <button
            key={row.id}
            type="button"
            onClick={row.onClick}
            className="w-full flex items-center justify-between  rounded-2xl bg-transparent border border-white/0 hover:bg-white/5 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-2xl bg-[#F33363]/10 border border-[#F33363]/25 flex items-center justify-center">
                <row.icon className="h-4 w-4 text-[#F33363]" />
              </div>
              <div className="text-sm font-semibold text-white uppercase tracking-widest">{row.label}</div>
            </div>
            <ChevronRight className="h-4 w-4 text-white/50" />
          </button>
        ))}
      </section>

      {/* Drawer for enabling */}
      <EnableExpressBookingDrawer
        open={enableDrawerOpen}
        onOpenChange={setEnableDrawerOpen}
        hideExperienceQuestions
        onSaveSuccess={refreshExpressBookingData}
        onSuccessNavigate={() => {
          setEnableDrawerOpen(false);
          navigate('/express-booking');
        }}
      />

      {/* Terms (T&C) */}
      <Sheet open={termsSheetOpen} onOpenChange={setTermsSheetOpen}>
        <div className="w-full">
          <TermsOfServicePage inSheet />
        </div>
      </Sheet>

      {/* Privacy */}
      <Sheet
        open={privacySheetOpen}
        onOpenChange={(open) => {
          setPrivacySheetOpen(open);
          if (!open) {
            setPrivacyIntroExpanded(false);
            setExpandedPrivacyCardId(null);
          }
        }}
      >
        <div className="w-full">
          <div className="flex flex-col items-center text-center">
            <div className="mt-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#4B0017]/50 border border-[#F33363]/35">
              <Lock className="h-5 w-5 text-[#F33363]" />
            </div>

            <h2 className="mt-4 text-xl font-bold text-white sm:text-2xl">
              Privacy Policy
            </h2>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-white/60 sm:text-base">
              Your privacy is important to us. This policy explains how we collect,
              use, and protect your personal information.
            </p>

            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setPrivacyIntroExpanded((v) => !v);
              }}
              className="mt-3 text-sm font-semibold text-[#3B82F6] underline underline-offset-4"
            >
              {privacyIntroExpanded ? 'Read less' : 'Read more'}
            </a>
          </div>

          {privacyIntroExpanded && (
            <div className="mt-3 rounded-2xl border border-white/10 bg-[#1C1C1E] px-4 py-3">
              <p className="text-sm leading-relaxed text-white/60">
                The website artist.starclinch.com (the &quot;Website&quot;) and the mobile application StarClinch Artist Cockpit (the &quot;App&quot;) are developed, owned, and operated by VINSM Globe Pvt Ltd. (&quot;Company&quot; / &quot;StarClinch&quot;). This Privacy Policy outlines how we collect, use, and protect your personal information across both platforms.
              </p>
            </div>
          )}

          <div className="mt-6 space-y-3">
            {privacyItems.map((item) => (
              <button
                key={item.n}
                type="button"
                onClick={() =>
                  setExpandedPrivacyCardId((prev) => (prev === item.n ? null : item.n))
                }
                aria-expanded={expandedPrivacyCardId === item.n}
                className="w-full rounded-2xl border border-[#F33363]/20 bg-[#0B0B0E] px-4 py-4 text-left"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="mt-0.5 inline-flex h-8 w-8 aspect-square items-center justify-center rounded-full border border-[#F33363]/30 bg-[#4B0017]/50">
                      <span className="text-sm font-semibold text-[#F33363]">
                        {item.n}
                      </span>
                    </div>

                    <div className="text-left">
                      <div className="text-sm font-semibold text-white">{item.title}</div>
                    </div>
                  </div>

                  <ChevronDown
                    className={`h-5 w-5 text-white/70 transition-transform ${expandedPrivacyCardId === item.n ? 'rotate-180' : ''
                      }`}
                  />
                </div>

                {expandedPrivacyCardId === item.n && (
                  <div className="mt-3">{item.expanded}</div>
                )}
              </button>
            ))}
          </div>
        </div>
      </Sheet>

      <AnimatePresence>
        {locationToDelete != null && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => (isDeleting ? null : setLocationToDelete(null))}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-sm bg-[#1C1C1E] border border-[#3A3A3C] rounded-[2.5rem] p-8 shadow-2xl"
            >
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="text-2xl font-black text-center text-white tracking-tight mb-2">
                Delete Location?
              </h3>
              <p className="text-[#8E8E93] text-center text-sm mb-8">
                Are you sure you want to delete this location? This action cannot be undone.
              </p>
              <div className="bg-[#0B0B0D] rounded-2xl p-4 mb-8 border border-[#3A3A3C]">
                <p className="text-xs font-black text-[#5A5A5E] uppercase tracking-widest mb-1">
                  Location
                </p>
                <p className="text-sm font-bold text-white">{locationToDelete.pincode}</p>
                <p className="text-xs text-[#8E8E93]">
                  {[locationToDelete.city, locationToDelete.state].filter(Boolean).join(', ')}
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  className="w-full py-4 rounded-2xl font-bold bg-red-500 hover:bg-red-600 transition-colors border-none disabled:opacity-60"
                  onClick={handleDeleteLocation}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Yes, Delete Location'}
                </button>
                <button
                  type="button"
                  onClick={() => setLocationToDelete(null)}
                  disabled={isDeleting}
                  className="w-full py-4 text-[#8E8E93] font-bold hover:text-white transition-colors disabled:opacity-60"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

